import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SidebarComponent } from './layout/sidebar/sidebar.component';
import { HeaderComponent } from './layout/header/header.component';
import { TenantContextService, TenantService } from './core/services';
import { AuthService } from './core/auth/auth.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, HeaderComponent],
  template: `
    @if (showChrome()) {
      <div class="app-shell">
        <app-sidebar [(collapsed)]="sidebarCollapsed" />
        <div class="main-area">
          <app-header>
            {{ pageTitle() }}
          </app-header>
          <div class="content-scroll">
            <router-outlet />
          </div>
        </div>
      </div>
    } @else {
      <router-outlet />
    }
  `,
  styles: [`
    .app-shell {
      display: flex;
      height: 100vh;
      overflow: hidden;
    }
    .main-area {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .content-scroll {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 24px 28px;
    }
  `],
})
export class App implements OnInit {
  private ctx = inject(TenantContextService);
  private tenantSvc = inject(TenantService);
  private auth = inject(AuthService);
  private router = inject(Router);

  sidebarCollapsed = false;
  pageTitle = signal('Dashboard');

  private readonly currentUrl = signal<string>(this.router.url);
  readonly showChrome = computed(() => {
    if (!this.auth.isLoggedIn()) return false;
    const url = this.currentUrl();
    return !url.startsWith('/login');
  });

  constructor() {
    this.auth.init();

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));

    effect(() => {
      if (this.auth.isLoggedIn()) {
        this.loadTenants();
      }
    });
  }

  ngOnInit(): void {
    if (this.auth.isLoggedIn()) {
      this.loadTenants();
    }
  }

  private loadTenants(): void {
    this.tenantSvc.list({ pageSize: 100 }).subscribe({
      next: (res) => this.ctx.setTenants(res.items),
      error: () => {},
    });
  }
}
